import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Bug, Send, Paperclip, MonitorPlay, Square, X, Minus, Maximize2, FileImage, FileVideo, File as FileIcon, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { reportBug, uploadBugChunk } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { getSessionReplayUrl } from "@/lib/posthog";

const fileIcon = (type) => {
  if (type?.startsWith("image/")) return <FileImage className="w-3.5 h-3.5" />;
  if (type?.startsWith("video/")) return <FileVideo className="w-3.5 h-3.5" />;
  return <FileIcon className="w-3.5 h-3.5" />;
};

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)}MB` : `${Math.ceil(b / 1024)}KB`);

export const ReportBugDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("normal");
  const [destination, setDestination] = useState("slack");
  const [files, setFiles] = useState([]);
  const [recording, setRecording] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);

  const addFiles = (list) => {
    const incoming = [...list].filter((f) => f.size <= 60 * 1024 * 1024);
    if (incoming.length < list.length) toast.error("Files over 60MB were skipped");
    setFiles((prev) => [...prev, ...incoming].slice(0, 5));
  };

  const onPaste = (e) => {
    const imgs = [...(e.clipboardData?.items || [])]
      .filter((i) => i.type.startsWith("image/"))
      .map((i) => i.getAsFile())
      .filter(Boolean)
      .map((f) => new File([f], `pasted-screenshot-${Date.now()}.png`, { type: f.type }));
    if (imgs.length) {
      addFiles(imgs);
      toast.success("Screenshot attached from clipboard");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const rec = new MediaRecorder(stream, { mimeType: "video/webm", videoBitsPerSecond: 1200000 });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "video/webm" });
        addFiles([new File([blob], `screen-recording-${Date.now()}.webm`, { type: "video/webm" })]);
        setRecording(false);
        setMinimized(false);
        toast.success("Screen recording attached");
      };
      stream.getVideoTracks()[0].onended = () => { if (rec.state !== "inactive") rec.stop(); };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setMinimized(true);
      toast.info("Recording started — the bug report is minimised so you can show the issue. Click Stop when done.");
      setTimeout(() => { if (rec.state === "recording") rec.stop(); }, 120000);
    } catch {
      toast.error("Screen recording isn't available in this browser");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const submit = async () => {
    if (!description.trim()) {
      toast.error("Please describe the bug");
      return;
    }
    setSending(true);
    try {
      // Big files (recordings) go up in 2MB chunks to dodge proxy limits
      const CHUNK = 2 * 1024 * 1024;
      const bigFiles = files.filter((f) => f.size > 4 * 1024 * 1024);
      const smallFiles = files.filter((f) => f.size <= 4 * 1024 * 1024);
      const refs = [];
      const totalBig = bigFiles.reduce((s, f) => s + f.size, 0);
      let sentBytes = 0;
      for (const f of bigFiles) {
        const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const total = Math.ceil(f.size / CHUNK);
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("chunk_index", i);
          fd.append("total_chunks", total);
          fd.append("filename", f.name);
          fd.append("chunk", f.slice(i * CHUNK, (i + 1) * CHUNK));
          // Retry each chunk twice — a single transient edge/network blip
          // shouldn't kill the whole report (prod RCA, 27 Jul 2026)
          let lastErr = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              await uploadBugChunk(fd);
              lastErr = null;
              break;
            } catch (e) {
              lastErr = e;
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
            }
          }
          if (lastErr) throw lastErr;
          sentBytes += Math.min(CHUNK, f.size - i * CHUNK);
          setUploadPct(Math.round((sentBytes / totalBig) * 100));
        }
        refs.push({ upload_id: uploadId, filename: f.name, content_type: f.type });
      }
      setUploadPct(null);

      const res = await reportBug({
        description: description.trim(),
        severity,
        destination,
        page_url: window.location.href,
        reporter_name: user?.name,
        reporter_email: user?.email,
        session_replay_url: getSessionReplayUrl(),
        uploaded_refs: refs.length ? JSON.stringify(refs) : null,
      }, smallFiles);
      const destLabel = destination === "slack" ? "the #portal-debugging Slack channel" : "the dev team";
      if (destination === "slack" && res.slack_posted === false) {
        toast.warning("Bug logged, but the Slack post failed — the dev team will still see it in the portal log");
      } else if (destination === "email" && res.emailed === false) {
        toast.warning("Bug logged, but the email couldn't be sent — the dev team will still see it in the portal log");
      } else if (res.failed_uploads?.length) {
        toast.warning(`Bug submitted, but ${res.failed_uploads.join(", ")} couldn't be stored`);
      } else {
        toast.success(`Bug report sent to ${destLabel} — thank you!`);
      }
      setDescription("");
      setSeverity("normal");
      setFiles([]);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Failed to submit bug report${detail ? ` — ${detail}` : ""}`);
      console.error(err);
    } finally {
      setSending(false);
      setUploadPct(null);
    }
  };

  return (
    <>
      {/* Minimised floating pill — keeps the portal usable while recording */}
      {open && minimized && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 bg-white border border-red-200 shadow-lg rounded-full pl-4 pr-2 py-2" data-testid="bug-minimized-pill">
          {recording ? (
            <>
              <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
              <span className="text-sm font-medium text-red-700">Recording…</span>
              <Button size="sm" onClick={stopRecording} className="h-7 bg-red-600 hover:bg-red-700 rounded-full" data-testid="bug-pill-stop-btn">
                <Square className="w-3.5 h-3.5 mr-1" /> Stop
              </Button>
            </>
          ) : (
            <>
              <Bug className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium">Bug report (draft)</span>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setMinimized(false)} className="h-7 w-7 p-0 rounded-full" title="Expand" data-testid="bug-pill-expand-btn">
            <Maximize2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      <Dialog open={open && !minimized} onOpenChange={(v) => { if (!v && recording) { setMinimized(true); return; } if (!v) setMinimized(false); onOpenChange(v); }}>
        <DialogContent className="max-w-md" data-testid="report-bug-dialog">
          <button
            onClick={() => setMinimized(true)}
            className="absolute right-10 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
            title="Minimise — keep working in the portal"
            data-testid="bug-minimize-btn"
          >
            <Minus className="w-4 h-4" />
          </button>
          <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Bug className="w-5 h-5" /> Report a bug
          </DialogTitle>
          <DialogDescription>
            Describe what went wrong — the page you're on is captured automatically. You can attach screenshots (or paste them straight in) and record your screen to show the issue.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            placeholder="What happened? What did you expect to happen? (paste a screenshot here with Ctrl+V)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onPaste={onPaste}
            rows={5}
            data-testid="bug-description-input"
          />
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="w-full" data-testid="bug-severity-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low — cosmetic / minor</SelectItem>
              <SelectItem value="normal">Normal — something isn't working right</SelectItem>
              <SelectItem value="urgent">Urgent — blocking my work</SelectItem>
            </SelectContent>
          </Select>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Send this report to</p>
            <div className="grid grid-cols-2 gap-2" data-testid="bug-destination-toggle">
              <button
                type="button"
                onClick={() => setDestination("slack")}
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                  destination === "slack"
                    ? "border-red-500 bg-red-50 text-red-700 font-medium"
                    : "border-muted text-muted-foreground hover:bg-slate-50"
                }`}
                data-testid="bug-dest-slack"
              >
                <MessageSquare className="w-4 h-4" /> Slack
              </button>
              <button
                type="button"
                onClick={() => setDestination("email")}
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                  destination === "email"
                    ? "border-red-500 bg-red-50 text-red-700 font-medium"
                    : "border-muted text-muted-foreground hover:bg-slate-50"
                }`}
                data-testid="bug-dest-email"
              >
                <Mail className="w-4 h-4" /> Email
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              {destination === "slack"
                ? "Posts to the #portal-debugging Slack channel."
                : "Emails the dev team at portal@simplyfunded.co.uk."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf"
              className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              data-testid="bug-file-input"
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="bug-attach-btn">
              <Paperclip className="w-4 h-4 mr-1.5" /> Attach
            </Button>
            {recording ? (
              <Button type="button" size="sm" onClick={stopRecording} className="bg-red-600 hover:bg-red-700 animate-pulse" data-testid="bug-stop-record-btn">
                <Square className="w-4 h-4 mr-1.5" /> Stop recording
              </Button>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={startRecording} data-testid="bug-record-btn">
                <MonitorPlay className="w-4 h-4 mr-1.5" /> Record screen
              </Button>
            )}
          </div>

          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {files.map((f, i) => (
                <Badge key={i} variant="outline" className="gap-1.5 pr-1 max-w-full">
                  {fileIcon(f.type)}
                  <span className="truncate max-w-[160px]">{f.name}</span>
                  <span className="text-muted-foreground">{fmtSize(f.size)}</span>
                  <button onClick={() => setFiles((prev) => prev.filter((_, x) => x !== i))} className="hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 items-center">
            {uploadPct != null && (
              <span className="text-xs text-muted-foreground mr-auto" data-testid="bug-upload-progress">
                Uploading recording… {uploadPct}%
              </span>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={recording || sending}>Cancel</Button>
            <Button onClick={submit} disabled={sending || recording} className="bg-red-600 hover:bg-red-700" data-testid="bug-submit-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {sending ? (uploadPct != null ? `Uploading ${uploadPct}%` : "Sending…") : "Submit"}
            </Button>
          </div>
        </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Opens the global bug-report dialog (rendered in Layout) from anywhere —
// wizards keep their state/open error visible while the report is filed.
export const openBugReport = () => window.dispatchEvent(new CustomEvent("sf-open-bug-report"));

export const WizardDebugButton = ({ className = "" }) => (
  <button
    type="button"
    onClick={openBugReport}
    title="Report a bug — this wizard stays open"
    data-testid="wizard-debug-btn"
    className={`inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-100 transition-colors ${className}`}
  >
    <Bug className="w-3.5 h-3.5" /> Debug
  </button>
);
